import { combineSubscribers, } from "@knyt/artisan";
import { build } from "./build/mod.js";
import { dom } from "./ElementBuilder.js";
import { removeAllChildren, update } from "./update/mod.js";
import { normalizeRenderResult } from "./utils/mod.js";
export class RenderController {
    get rootBuilder() {
        throw new Error("Property not implemented.");
    }
    #state$;
    #roots = new Array();
    /**
     * Removes roots that have been reclaimed by the garbage collector.
     */
    #removeReclaimedRoots() {
        for (let i = 0; i < this.#roots.length; i++) {
            const root = this.#roots[i].element.deref();
            if (!root) {
                this.#roots.splice(i, 1);
                i--;
            }
        }
    }
    #getRootByElement(element) {
        for (const root of this.#roots) {
            if (root.element.deref() === element) {
                return root;
            }
        }
        this.#removeReclaimedRoots();
        return undefined;
    }
    createRootSubscriber(auxiliary) {
        return (element) => {
            if (!element)
                return;
            const existingRoot = this.#getRootByElement(element);
            if (existingRoot) {
                // Update the auxiliary data, but don't update the element.
                // The element should only be updated when the state changes.
                existingRoot.auxiliary = auxiliary;
                return;
            }
            this.#roots.push({
                element: new WeakRef(element),
                auxiliary,
            });
            // All side-effects are run asynchronously
            queueMicrotask(() => {
                // Immediately update the root element with the current state
                // when a new root is added.
                this.insertRoot(element, auxiliary);
            });
        };
    }
    constructor(state$) {
        this.#state$ = new WeakRef(state$);
        state$.subscribe(this);
    }
    render(state, auxiliary) {
        return dom.fragment;
    }
    next(_state) {
        this.updateAllRoots();
    }
    async updateAllRoots() {
        const state = this.#state$.deref()?.value;
        if (!state)
            return;
        // We're using `forEach` here, because we want don't want to
        // wait for the updates to finish before continuing.
        this.#roots.forEach(async (root) => {
            const rootElement = root.element.deref();
            if (rootElement) {
                await update(rootElement, await normalizeRenderResult(this.render(state, root.auxiliary)));
            }
        });
        this.#removeReclaimedRoots();
    }
    async insertRoot(rootElement, auxiliary) {
        const state = this.#state$.deref()?.value;
        if (!state)
            return;
        const contentsDeclaration = await normalizeRenderResult(this.render(state, auxiliary));
        const contents = (await build(contentsDeclaration));
        if (!contents)
            return;
        removeAllChildren(rootElement);
        rootElement.appendChild(contents);
    }
    static instances = new WeakMap();
    static getInstance(state$) {
        let instance = this.instances.get(state$);
        if (!instance) {
            instance = new this(state$);
            this.instances.set(state$, instance);
        }
        return instance;
    }
    static declareRoot() {
        return {
            // TODO: Consider adding support for `children` here.
            $init: ({ state, auxiliary, ref }) => {
                const externalRoot$ = ref;
                const controller = this.getInstance(state);
                const controllerRoot$ = controller.createRootSubscriber(auxiliary);
                const root$ = combineSubscribers(externalRoot$, controllerRoot$);
                let rootBuilder = controller.rootBuilder;
                // NOTE: For some reason TypeScript doesn't infer the type of `rootBuilder`
                // correctly, and all of its methods end up being `any`. I've verified that
                // the types are correct. As a result, we have to cast it to `ElementBuilder`
                // to get some type safety back.
                //
                // it seems like the TypeScript compiler is reaching the limit of its type
                // inference, and it stops. Hopefully, this will go away when the Go-based
                // compiler is released.
                //
                // prettier-ignore
                rootBuilder = rootBuilder.$renderMode("opaque");
                // prettier-ignore
                rootBuilder = rootBuilder.$ref(root$);
                return rootBuilder;
            },
        };
    }
}
/**
 * @alpha Experimental API this API is subject to change without notice.
 */
export function createRenderController(root, lifecycle) {
    return class extends RenderController {
        get rootBuilder() {
            return root;
        }
        #lifecycleRenderFn;
        constructor(state$) {
            super(state$);
            this.#lifecycleRenderFn = lifecycle(state$);
        }
        render(state, auxiliary) {
            return this.#lifecycleRenderFn(state, auxiliary);
        }
    };
}
/**
 * A convenience function to declare a root for a RenderController.
 *
 * @remarks
 *
 * This function avoids having to write duplicate code/types, because TypeScript
 * is unable to reference the class type in the static method.
 *
 * @alpha Experimental API
 */
export function declareControlledRoot(ControllerConstructor) {
    return ControllerConstructor.declareRoot();
}
